// REST-Client für die maco-runner JIRA-Endpoints.
// Alle Calls gehen über /api/* -- in Dev gegen den Vite-Proxy zum FastAPI-
// Backend (localhost:8000), in Prod gegen die gleiche Origin via Reverse-Proxy.

export type JiraSyncEntry = {
  aend_id?: string;
  doctype?: string;
  ticket_key: string;
  jira_key: string;
  jira_url: string;
};

export type JiraSyncFailure = {
  ticket_key: string;
  aend_id?: string;
  doctype?: string;
  error: string;
};

export type JiraSyncResponse = {
  run_id: string;
  epic_key: string | null;
  epic_url: string | null;
  created: JiraSyncEntry[];
  failed: JiraSyncFailure[];
  skipped: unknown[];
};

export type JiraRunSummary = {
  run_id: string;
  epic_key: string | null;
  created_count: number;
  failed_count: number;
};

class JiraApiError extends Error {
  constructor(public status: number, public detail: string) {
    super(`JIRA-API (${status}): ${detail}`);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  // Lazy-Import des Auth-Moduls: vermeidet Circular-Deps und erlaubt diesem
  // Modul, im Test ohne Keycloak-Stub zu laufen.
  const { getToken } = await import("./auth");
  const token = getToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init?.headers as Record<string, string> | undefined ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    // FastAPI legt Fehlertext unter `detail`. Wir geben den Status-Code
    // separat zurück, damit die UI z.B. 503 ("Backend nicht konfiguriert")
    // vom 404 ("Run unbekannt") visuell unterscheiden kann.
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body?.detail ?? detail;
    } catch {
      /* Body war kein JSON -- statusText reicht */
    }
    throw new JiraApiError(res.status, detail);
  }
  return res.json() as Promise<T>;
}

export const jiraApi = {
  health: () => request<{ status: string }>("/api/health"),
  listRuns: () => request<{ runs: JiraRunSummary[] }>("/api/jira/runs"),
  getResult: (runId: string) =>
    request<JiraSyncResponse>(`/api/jira/result/${encodeURIComponent(runId)}`),
  syncOne: (runId: string, ticketKey: string, edits?: { title?: string; description?: string }) =>
    request<JiraSyncResponse>("/api/jira/sync-one", {
      method: "POST",
      body: JSON.stringify({ run_id: runId, ticket_key: ticketKey, edits, dry_run: false }),
    }),
  syncBatch: (runId: string, decisions: Record<string, { state: string; edits?: { title?: string; description?: string } }>) =>
    request<JiraSyncResponse>("/api/jira/sync-batch", {
      method: "POST",
      body: JSON.stringify({ run_id: runId, decisions, dry_run: false }),
    }),
};

export { JiraApiError };
